import { ThemeProvider, useTheme } from "@mui/material/styles";
import { Badge, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import clsx from "clsx";
import { memo } from "react";
import {
  selectFuseCurrentLayoutConfig,
  selectToolbarTheme,
} from "@fuse/core/FuseSettings/fuseSettingsSlice";
import { Layout1ConfigDefaultsType } from "app/theme-layouts/layout1/Layout1Config";
import NavbarToggleButton from "app/theme-layouts/shared-components/navbar/NavbarToggleButton";
import { selectFuseNavbar } from "app/theme-layouts/shared-components/navbar/navbarSlice";
import { useAppSelector } from "app/store/hooks";
import UserMenu from "../../shared-components/UserMenu";
import LanguageSwitcher from "app/theme-layouts/shared-components/LanguageSwitcher";

import AdjustFontSize from "app/theme-layouts/shared-components/AdjustFontSize";
import FullScreenToggle from "app/theme-layouts/shared-components/FullScreenToggle";
import NotificationButton from "app/theme-layouts/shared-components/NotificationButton";
import { selectUser } from "src/app/auth/user/store/userSlice";
import { useAuthInfo } from "@propelauth/react";
import { Inbox } from "@novu/react";
import { useNavigate } from "react-router-dom";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { useTranslation } from "react-i18next";

type ToolbarLayout1Props = {
  className?: string;
};

const appearance = {
  elements: {
    bellIcon: {
      padding: "1rem",
      backgroundColor: "white",
      borderRadius: "50%",
    },
    notification: {
      backgroundColor: "white",
      fontSize: "1rem",
      borderRadius: "0.5rem",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    },
    inboxStatus__title: {
      fontSize: "1rem",
    },
    inboxHeader: {
      display: "none",
    },
    notificationListEmptyNotice: {
      fontSize: "1.2rem",
    },
  },
  styles: {
    "& div[class*='nt-relative'][class*='nt-flex'][class*='nt-shrink-0']": {
      display: "none !important",
    },
  },
};

/**
 * The toolbar layout 1.
 */
function ToolbarLayout1(props: ToolbarLayout1Props) {
  const { className } = props;
  const config = useAppSelector(
    selectFuseCurrentLayoutConfig
  ) as Layout1ConfigDefaultsType;
  const navbar = useAppSelector(selectFuseNavbar);
  const toolbarTheme = useAppSelector(selectToolbarTheme);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const user = useAppSelector(selectUser);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const orgIds = Object.keys(user.org_id_to_org_member_info);
  const firstOrgId = orgIds[0];

  return (
    <ThemeProvider theme={toolbarTheme}>
      <AppBar
        id="fuse-toolbar"
        className={clsx("relative z-20 flex", className)}
        color="default"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? toolbarTheme.palette.background.paper
              : toolbarTheme.palette.background.default,
        }}
        position="static"
        elevation={0}
      >
        <Toolbar className="min-h-48 p-0 md:min-h-64 shadow">
          <div className="flex flex-1 px-0 justify-between mr-10 gap-6 items-center">
            {config.navbar.display && config.navbar.position === "left" && (
              <>
                {!isMobile && (
                  <>
                    {(config.navbar.style === "style-3" ||
                      config.navbar.style === "style-3-dense") && (
                      <NavbarToggleButton className="mx-0 h-40 w-40 p-0" />
                    )}

                    {config.navbar.style === "style-1" && !navbar.open && (
                      <NavbarToggleButton className="mx-0 h-40 w-40 p-0" />
                    )}
                  </>
                )}

                {isMobile && (
                  <NavbarToggleButton className="mx-0 h-40 w-40 p-0 sm:mx-8" />
                )}
              </>
            )}
          </div>

          <div className="flex h-full items-center overflow-x-auto px-8">
            <LanguageSwitcher />
            <AdjustFontSize />

            {/* <NavigationSearch /> */}
            {/* <QuickPanelToggleButton /> */}
            {/* <NotificationButton /> */}
            {user && (
              <Inbox
                applicationIdentifier="lJqtY_URuYl5"
                subscriber={`${firstOrgId}_${user.uid}`}
                // subscriberId={user.userId}
                backendUrl={
                  import.meta.env.VITE_NOVU_BACKEND_URL ||
                  "https://eu.api.novu.co"
                }
                socketUrl={
                  import.meta.env.VITE_NOVU_SOCKET_URL ||
                  "https://eu.ws.novu.co"
                }
                routerPush={(path: string) => navigate(path)}
                renderBell={(unreadCount) => {
                  return (
                    <IconButton
                      className={clsx("h-40 w-40", className)}
                      aria-controls="font-size-menu"
                      color="inherit"
                    >
                      <Badge badgeContent={unreadCount} color="error">
                        <FuseSvgIcon>heroicons-outline:bell</FuseSvgIcon>
                      </Badge>
                    </IconButton>
                  );
                }}
                localization={{
                  // Filter dropdown options
                  "inbox.filters.dropdownOptions.unread": "Unread only",
                  "inbox.filters.dropdownOptions.default": "Unread & read",
                  "inbox.filters.dropdownOptions.archived": "Archived",

                  // Filter labels
                  "inbox.filters.labels.unread": "Unread",
                  "inbox.filters.labels.default": "Inbox",
                  "inbox.filters.labels.archived": "Archived",

                  // Notifications section
                  "notifications.emptyNotice": "No Notifications",
                  "notifications.actions.readAll": "Mark all as read",
                  "notifications.actions.archiveAll": "Archive all",
                  "notifications.actions.archiveRead": "Archive read",
                  "notifications.newNotifications": ({
                    notificationCount,
                  }: {
                    notificationCount: number;
                  }) =>
                    `${notificationCount > 99 ? "99+" : notificationCount} new ${
                      notificationCount === 1 ? "notification" : "notifications"
                    }`,

                  // Individual notification actions
                  "notification.actions.read.tooltip": "Mark as read",
                  "notification.actions.unread.tooltip": "Mark as unread",
                  "notification.actions.archive.tooltip": "Archive",
                  "notification.actions.unarchive.tooltip": "Unarchive",

                  // Preferences section
                  "preferences.title": "Preferences",
                  "preferences.emptyNotice":
                    "No notification specific preferences yet.",
                  "preferences.global": "Global Preferences",
                  "preferences.workflow.disabled.notice":
                    "Contact admin to enable subscription management for this critical notification.",
                  "preferences.workflow.disabled.tooltip":
                    "Contact admin to edit",

                  // Set locale
                  locale: "en-US",

                  // Dynamic localization for workflow names
                  dynamic: {
                    // use the workflowId as a key to localize the workflow name
                    "comment-on-post": "Post comments",
                  },
                }}
                appearance={appearance}
              />
            )}
            <FullScreenToggle />
            <UserMenu />
          </div>

          {config.navbar.display && config.navbar.position === "right" && (
            <>
              {!isMobile && !navbar.open && (
                <NavbarToggleButton className="mx-0 h-40 w-40 p-0" />
              )}

              {isMobile && (
                <NavbarToggleButton className="mx-0 h-40 w-40 p-0 sm:mx-8" />
              )}
            </>
          )}
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
}

export default memo(ToolbarLayout1);
